import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Session, Student, Tutor } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { ActivityClock } from '../components/ActivityClock'
import { Plus, Calendar, Clock, Search, Upload, Download, FileDown, Filter, ChevronDown, CheckCircle, XCircle, FileText, X, Trash2, Ban, Lock, ShieldAlert } from 'lucide-react'
import { format } from 'date-fns'
import { logActivity, createNotification, resolveNotificationsFor, notifyLowBalance } from '../lib/activity'
import { syncConsultingCredits } from '../lib/consulting'
import { StudentBalance, approvalNeedsReason, fetchStudentBalances, isBalanceGuardError, isLogBlocked, sessionsLabel } from '../lib/balance'
import { hasAdminRights } from '../lib/roles'

interface ExtendedSession extends Session {
  studentName?: string
  tutorName?: string
  reportUrl?: string
  reportFilename?: string
  createdAt?: string
  approvalStatus?: 'pending' | 'approved' | 'rejected'
  approvedBy?: string | null
  approvedAt?: string | null
  overBalance?: boolean
  overrideReason?: string | null
}

// Sessions component - manages tutoring sessions with approval workflow
export function Sessions() {
  const { profile } = useAuth()
  const [sessions, setSessions] = useState<ExtendedSession[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [tutors, setTutors] = useState<Tutor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [viewingNotesSession, setViewingNotesSession] = useState<ExtendedSession | null>(null)
  const [visibleColumns, setVisibleColumns] = useState({
    date: true,
    timestamp: true,
    student: true,
    tutor: true,
    duration: true,
    time: true,
    status: true,
    notes: true,
    report: true,
    actions: false
  })
  const [formData, setFormData] = useState({
    studentId: '',
    tutorId: '',
    startedAt: '',
    endedAt: '',
    notes: ''
  })
  const [isUsingTemplate, setIsUsingTemplate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [cancellingSession, setCancellingSession] = useState<ExtendedSession | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  // Student IDs that are frozen for new sessions because they have a pending
  // per-student rate-change request awaiting admin approval (Phase 3).
  const [frozenStudentIds, setFrozenStudentIds] = useState<Set<string>>(new Set())
  const [balances, setBalances] = useState<Map<string, StudentBalance>>(new Map())
  const [overrideReason, setOverrideReason] = useState('')
  const [approvingSession, setApprovingSession] = useState<ExtendedSession | null>(null)
  const [approveReason, setApproveReason] = useState('')
  const [approveSubmitting, setApproveSubmitting] = useState(false)

  const actor = profile ? { id: profile.id, email: profile.email, role: profile.role } : null

  // Generate session report template with auto-fill
  const generateSessionTemplate = (useCurrentData = true) => {
    // Get the actual names and format times properly
    const studentName = useCurrentData && formData.studentId
      ? students.find(s => s.id === formData.studentId)?.name || 'Student ID: ' + formData.studentId
      : '[Select Student Above]'

    const tutorName = useCurrentData && formData.tutorId
      ? tutors.find(t => t.id === formData.tutorId)?.name || 'Tutor ID: ' + formData.tutorId
      : '[Select Tutor Above]'

    const startDate = useCurrentData && formData.startedAt
      ? new Date(formData.startedAt).toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : '[Select Date Above]'

    const startTime = useCurrentData && formData.startedAt
      ? new Date(formData.startedAt).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      : '[Select Start Time Above]'

    const endTime = useCurrentData && formData.endedAt
      ? new Date(formData.endedAt).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
      : '[Select End Time Above]'

    return `PRIVATE SESSION REPORT

Date: ${startDate}
Start Time: ${startTime}
End Time: ${endTime}
Student: ${studentName}
Tutor: ${tutorName}

SESSION PREPARATION - Prior to Session
=====================================
Subject:

What will you work on?
☐ Homework  ☐ Assignment  ☐ Test Prep  ☐ Other: ________

Detailed Description:
(Example: The session focused on preparing the student for Science 10 final. Focus was primarily on Chemistry and Physics as these are where the student struggles most.)


SESSION FEEDBACK - After the Session
=====================================
What did you work on?
☐ Homework  ☐ Assignment  ☐ Test Prep  ☐ Other: ________
(Did you cover all the prepared parts?)


FEEDBACK:
---------
Did the student understand the concept/complete the assignment/prepare for the test? Please describe:
(Example: The student effectively responded to practice questions and was very cooperative with the tutor. She made sure to inform the tutor of what she was struggling with and thus, effectively prepared for the test alongside the tutor.)


Was the student receptive? Please describe the student's state:
(Example: The student was a little quiet but always spoke up when need be. She was responsive and informed me of whether she understood or was okay with the concept or not. The student seemed a little nervous about the final but always listened well and respectfully. Overall, she was open, willing and had the correct mindset.)


FOLLOW UP:
----------
Does the student need to put in more work on today's lesson? Please describe:
(Example: As we only had an hour, I feel like the student required a lot more revision and ways to affirm or test her concepts. Moreover, with the lack of time, not everything was covered.)


Please list any upcoming tests, quizzes, or assignments:
(Example: As Monday is the final, there will be no more tests for the student regarding this topic. However, if the student fails, she has informed me that she will be redoing this course online during the summer and will therefore further require assistance and tutoring.)


Additional Notes:
`
  }

  useEffect(() => {
    fetchSessions()
    fetchStudents()
    fetchTutors()
    fetchFrozenStudents()
    fetchBalances()
  }, [])

  const fetchBalances = async () => {
    try {
      const list = await fetchStudentBalances()
      setBalances(new Map(list.map(b => [b.id, b])))
    } catch (error) {
      console.error('Error fetching student balances:', error)
    }
  }

  // Load students with a pending rate-change request — logging is frozen for them.
  const fetchFrozenStudents = async () => {
    try {
      const { data, error } = await supabase
        .from('rate_change_requests')
        .select('student_id')
        .eq('status', 'pending')
      if (error) throw error
      setFrozenStudentIds(new Set((data || []).map((r) => r.student_id)))
    } catch (error) {
      console.error('Error fetching frozen students:', error)
    }
  }

  // Set actions column visibility based on role
  useEffect(() => {
    if (hasAdminRights(profile?.role)) {
      setVisibleColumns(prev => ({ ...prev, actions: true }))
    }
  }, [profile])

  // Auto-select current tutor when tutors are loaded
  useEffect(() => {
    if (profile?.role === 'tutor' && profile?.email && tutors.length > 0) {
      const currentTutor = tutors.find(t =>
        t.email?.toLowerCase() === profile.email?.toLowerCase()
      )
      if (currentTutor && formData.tutorId !== currentTutor.id) {
        setFormData(prev => ({ ...prev, tutorId: currentTutor.id }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutors, profile])

  // Auto-update template when form data changes
  useEffect(() => {
    if (isUsingTemplate && (formData.studentId || formData.tutorId || formData.startedAt || formData.endedAt)) {
      const updatedTemplate = generateSessionTemplate()
      // Only update if the notes still look like a template (hasn't been heavily edited)
      if (formData.notes.includes('PRIVATE SESSION REPORT')) {
        setFormData(prev => ({ ...prev, notes: updatedTemplate }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.studentId, formData.tutorId, formData.startedAt, formData.endedAt, isUsingTemplate])

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select('*')
        .order('started_at', { ascending: false })

      if (error) throw error

      const formattedSessions = data?.map(session => ({
        id: session.id,
        studentId: session.student_id,
        tutorId: session.tutor_id,
        startedAt: session.started_at,
        endedAt: session.ended_at,
        minutes: session.minutes,
        notes: session.notes,
        attachmentFilename: session.attachment_filename,
        reportUrl: session.report_url,
        reportFilename: session.report_filename,
        createdAt: session.created_at,
        approvalStatus: session.approval_status || 'pending',
        approvedBy: session.approved_by,
        approvedAt: session.approved_at,
        sessionType: session.session_type || 'tutoring',
        lifecycleStatus: session.lifecycle_status || 'completed',
        isFree: session.is_free || false,
        cancelledAt: session.cancelled_at,
        cancelReason: session.cancel_reason,
        consultingCreditId: session.consulting_credit_id,
        overBalance: session.over_balance || false,
        overrideReason: session.override_reason ?? null
      })) || []

      setSessions(formattedSessions)
    } catch (error) {
      console.error('Error fetching sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchStudents = async () => {
    try {
      const { data } = await supabase.from('students').select('id, name')
      setStudents(data?.map(s => ({
        id: s.id,
        name: s.name,
        tutorId: '',
        balanceMinutes: 0
      })) || [])
    } catch (error) {
      console.error('Error fetching students:', error)
    }
  }

  const fetchTutors = async () => {
    try {
      const { data } = await supabase.from('tutors').select('id, name, email')
      console.log('Fetched tutors:', data)
      setTutors(data?.map(t => ({
        id: t.id,
        name: t.name,
        email: t.email || '',
        subjects: [],
        hourlyRate: 0,
        totalMinutesAvailable: 0,
        totalMinutesUsed: 0
      })) || [])
    } catch (error) {
      console.error('Error fetching tutors:', error)
    }
  }

  const calculateMinutes = (start: string, end: string) => {
    const startDate = new Date(start)
    const endDate = new Date(end)
    return Math.round((endDate.getTime() - startDate.getTime()) / 60000)
  }

  const exportToCSV = () => {
    const csvData = filteredSessions.map(session => {
      const row: any = {}
      if (visibleColumns.date) {
        row.Date = format(new Date(session.startedAt), 'MMM d, yyyy')
      }
      if (visibleColumns.timestamp) {
        row.Timestamp = session.createdAt
          ? format(new Date(session.createdAt), 'MMM d, yyyy h:mm a')
          : 'N/A'
      }
      if (visibleColumns.student) {
        row.Student = session.studentName || ''
      }
      if (visibleColumns.tutor) {
        row.Tutor = session.tutorName || ''
      }
      if (visibleColumns.duration) {
        row.Duration = `${session.minutes} minutes`
      }
      if (visibleColumns.time) {
        row['Start Time'] = format(new Date(session.startedAt), 'h:mm a')
        row['End Time'] = format(new Date(session.endedAt), 'h:mm a')
      }
      if (visibleColumns.notes) {
        row.Notes = session.notes || ''
      }
      if (visibleColumns.report) {
        row['Has Report'] = session.reportUrl ? 'Yes' : 'No'
      }
      return row
    })

    if (csvData.length === 0) {
      alert('No data to export')
      return
    }

    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header =>
          `"${String(row[header as keyof typeof row] || '').replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `sessions_${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleUploadReport = async (sessionId: string, file: File) => {
    try {
      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${sessionId}_${Date.now()}.${fileExt}`
      const { error: uploadError } = await supabase.storage
        .from('session-reports')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('session-reports')
        .getPublicUrl(fileName)

      // Update session with report URL
      const { error: updateError } = await supabase
        .from('sessions')
        .update({
          report_url: urlData.publicUrl,
          report_filename: file.name
        })
        .eq('id', sessionId)

      if (updateError) throw updateError

      // Refresh sessions
      fetchSessions()
      alert('Report uploaded successfully!')
    } catch (error) {
      console.error('Error uploading report:', error)
      alert('Failed to upload report')
    }
  }

  const handleApproveSession = async (sessionId: string, studentId: string, minutes: number, reason?: string): Promise<'approved' | 'needs_reason' | 'failed'> => {
    if (!profile || !hasAdminRights(profile.role)) {
      alert('Only admins can approve sessions')
      return 'failed'
    }

    try {
      const { error: updateError } = await supabase
        .from('sessions')
        .update({
          approval_status: 'approved',
          approved_by: profile.email,
          approved_at: new Date().toISOString(),
          ...(reason ? { override_reason: reason } : {})
        })
        .eq('id', sessionId)

      if (updateError) throw updateError

      // Get student balance
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('balance_minutes')
        .eq('id', studentId)
        .single()

      if (studentError) throw studentError

      // Deduct minutes from student
      const newBalance = (studentData?.balance_minutes || 0) - minutes
      const { error: balanceError } = await supabase
        .from('students')
        .update({ balance_minutes: newBalance })
        .eq('id', studentId)

      if (balanceError) throw balanceError

      // Add ledger entry
      await supabase.from('minutes_ledger').insert({
        student_id: studentId,
        delta_minutes: -minutes,
        reason: 'session',
        related_id: sessionId,
        running_balance: newBalance,
        entered_by: profile.email,
        entered_by_role: 'admin'
      })

      const studentName = getStudentName(studentId)

      // Warn if this deduction left the student low / out of minutes.
      await notifyLowBalance(studentId, newBalance, studentName)

      // Clear the pending-approval alert for this session and log the event.
      await resolveNotificationsFor('sessions', sessionId, profile.email, 'session_approval')
      await logActivity({
        scope: 'sessions',
        action: 'session_approved',
        summary: `Approved ${minutes}-min session for ${studentName}`,
        entityTable: 'sessions',
        entityId: sessionId,
        metadata: { minutes, studentId },
        actor,
      })

      // Recompute consulting entitlement (every 2 approved tutoring sessions => 1 free).
      let creditMsg = ''
      try {
        const res = await syncConsultingCredits(studentId, { actor, studentName })
        if (res.granted > 0) {
          creditMsg = `\n\n🎁 ${studentName} just earned ${res.granted} free consulting session${res.granted > 1 ? 's' : ''}!`
        }
      } catch (e) {
        console.error('Consulting sync failed (non-fatal):', e)
      }

      alert(`Session approved! Minutes have been deducted.${creditMsg}`)
      fetchSessions()
      fetchBalances()
      return 'approved'
    } catch (error: any) {
      if (isBalanceGuardError(error)) fetchBalances()
      if (error?.hint === 'override_reason_required') return 'needs_reason'
      console.error('Error approving session:', error)
      alert(error?.message ? `Failed to approve session: ${error.message}` : 'Failed to approve session')
      return 'failed'
    }
  }

  const askApproveReason = (session: ExtendedSession) => {
    setApproveReason(session.overrideReason || '')
    setApprovingSession(session)
  }

  const requestApprove = async (session: ExtendedSession) => {
    const bal = balances.get(session.studentId)
    if (approvalNeedsReason(bal?.balanceMinutes, session.overBalance)) {
      askApproveReason(session)
      return
    }
    if (await handleApproveSession(session.id, session.studentId, session.minutes) === 'needs_reason') {
      askApproveReason(session)
    }
  }

  const confirmApproveWithReason = async () => {
    if (!approvingSession || !approveReason.trim()) return
    setApproveSubmitting(true)
    const ok = (await handleApproveSession(approvingSession.id, approvingSession.studentId, approvingSession.minutes, approveReason.trim())) === 'approved'
    setApproveSubmitting(false)
    if (ok) {
      setApprovingSession(null)
      setApproveReason('')
    }
  }

  const handleRejectSession = async (sessionId: string) => {
    if (!profile || !hasAdminRights(profile.role)) {
      alert('Only admins can reject sessions')
      return
    }

    try {
      const { error } = await supabase
        .from('sessions')
        .update({
          approval_status: 'rejected',
          approved_by: profile.email,
          approved_at: new Date().toISOString()
        })
        .eq('id', sessionId)

      if (error) throw error

      await resolveNotificationsFor('sessions', sessionId, profile.email, 'session_approval')
      await logActivity({
        scope: 'sessions',
        action: 'session_rejected',
        summary: `Rejected session`,
        entityTable: 'sessions',
        entityId: sessionId,
        actor,
      })

      alert('Session rejected. No minutes deducted.')
      fetchSessions()
    } catch (error) {
      console.error('Error rejecting session:', error)
      alert('Failed to reject session')
    }
  }

  const handleDeleteSession = async (session: ExtendedSession) => {
    if (!profile || !hasAdminRights(profile.role)) {
      alert('Only admins can delete sessions')
      return
    }

    // Only restore minutes if the session was approved AND not already cancelled
    // (a cancelled-approved session has already had its minutes restored).
    const willRestore = session.approvalStatus === 'approved' && session.lifecycleStatus !== 'cancelled'
    const confirmMessage = willRestore
      ? `Are you sure you want to delete this session?\n\nThis session was APPROVED and ${session.minutes} minutes were deducted from ${session.studentName}'s balance.\n\nDeleting will restore the minutes to the student.`
      : `Are you sure you want to delete this session for ${session.studentName}?`

    if (!window.confirm(confirmMessage)) return

    try {
      // If session was approved (and not already cancelled/restored), restore the minutes to the student
      if (willRestore) {
        // Get current student balance
        const { data: studentData, error: studentFetchError } = await supabase
          .from('students')
          .select('balance_minutes')
          .eq('id', session.studentId)
          .single()

        if (studentFetchError) throw studentFetchError

        // Restore the minutes
        const newBalance = (studentData?.balance_minutes || 0) + session.minutes
        const { error: balanceError } = await supabase
          .from('students')
          .update({ balance_minutes: newBalance })
          .eq('id', session.studentId)

        if (balanceError) throw balanceError

        // Add ledger entry for the restoration
        await supabase.from('minutes_ledger').insert({
          student_id: session.studentId,
          delta_minutes: session.minutes,
          reason: 'session',
          related_id: session.id,
          running_balance: newBalance,
          entered_by: profile.email,
          entered_by_role: 'admin',
          reference: 'Session deleted - minutes restored'
        })
      }

      // Delete related ledger entries for this session
      await supabase
        .from('minutes_ledger')
        .delete()
        .eq('related_id', session.id)
        .eq('reason', 'session')

      // Delete the session
      const { error: deleteError } = await supabase
        .from('sessions')
        .delete()
        .eq('id', session.id)

      if (deleteError) throw deleteError

      // Clear any notifications tied to this session and log the deletion.
      await resolveNotificationsFor('sessions', session.id, profile.email)
      await logActivity({
        scope: 'sessions',
        action: 'session_deleted',
        summary: `Deleted ${session.minutes}-min session for ${session.studentName}`,
        entityTable: 'sessions',
        entityId: session.id,
        metadata: { willRestore, minutes: session.minutes },
        actor,
      })

      // Deleting an approved (non-cancelled) tutoring session lowers the
      // qualifying count — revoke any surplus unclaimed consulting credits.
      if (willRestore) {
        try {
          await syncConsultingCredits(session.studentId, { actor, studentName: session.studentName })
        } catch (e) {
          console.error('Consulting sync after delete failed (non-fatal):', e)
        }
      }

      alert(willRestore
        ? 'Session deleted and minutes restored to student.'
        : 'Session deleted successfully.')
      fetchSessions()
    } catch (error) {
      console.error('Error deleting session:', error)
      alert('Failed to delete session')
    }
  }

  // Cancel a session: marks it cancelled (separate from approval). If the session was
  // already approved, the deducted minutes are restored to the student (the session is void).
  const handleCancelSession = async () => {
    if (!cancellingSession) return
    if (!profile || !hasAdminRights(profile.role)) {
      alert('Only admins can cancel sessions')
      return
    }

    const session = cancellingSession
    setCancelSubmitting(true)
    try {
      // Restore minutes only if it was an approved, not-yet-cancelled session.
      if (session.approvalStatus === 'approved' && session.lifecycleStatus !== 'cancelled') {
        const { data: studentData, error: studentFetchError } = await supabase
          .from('students')
          .select('balance_minutes')
          .eq('id', session.studentId)
          .single()

        if (studentFetchError) throw studentFetchError

        const newBalance = (studentData?.balance_minutes || 0) + session.minutes
        const { error: balanceError } = await supabase
          .from('students')
          .update({ balance_minutes: newBalance })
          .eq('id', session.studentId)

        if (balanceError) throw balanceError

        await supabase.from('minutes_ledger').insert({
          student_id: session.studentId,
          delta_minutes: session.minutes,
          reason: 'session',
          related_id: session.id,
          running_balance: newBalance,
          entered_by: profile.email,
          entered_by_role: 'admin',
          reference: 'Session cancelled - minutes restored'
        })
      }

      const { error: updateError } = await supabase
        .from('sessions')
        .update({
          lifecycle_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancel_reason: cancelReason.trim() || null
        })
        .eq('id', session.id)

      if (updateError) throw updateError

      const wasApproved = session.approvalStatus === 'approved' && session.lifecycleStatus !== 'cancelled'

      await resolveNotificationsFor('sessions', session.id, profile.email)
      await logActivity({
        scope: 'sessions',
        action: 'session_cancelled',
        summary: `Cancelled session for ${session.studentName}${wasApproved ? ` (restored ${session.minutes} min)` : ''}`,
        entityTable: 'sessions',
        entityId: session.id,
        metadata: { reason: cancelReason.trim() || null, wasApproved },
        actor,
      })

      // Cancelling an approved tutoring session lowers the qualifying count.
      if (wasApproved) {
        try {
          await syncConsultingCredits(session.studentId, { actor, studentName: session.studentName })
        } catch (e) {
          console.error('Consulting sync after cancel failed (non-fatal):', e)
        }
      }

      setCancellingSession(null)
      setCancelReason('')
      fetchSessions()
    } catch (error) {
      console.error('Error cancelling session:', error)
      alert('Failed to cancel session')
    } finally {
      setCancelSubmitting(false)
    }
  }

  // Validate the session form. Returns an error message string, or null if valid.
  const validateSessionForm = () => {
    if (!formData.studentId) return 'Please select a student.'
    if (frozenStudentIds.has(formData.studentId)) {
      const name = getStudentName(formData.studentId)
      return `${name} has a pending rate-change request. Logging is paused for this student until an admin approves or rejects it.`
    }
    const bal = balances.get(formData.studentId)
    if (isLogBlocked(bal)) {
      const name = getStudentName(formData.studentId)
      if (!profile || !hasAdminRights(profile.role)) {
        return `${name} has no minutes left. Ask the office to collect a top-up before logging more sessions.`
      }
      if (!overrideReason.trim()) return `${name} has no minutes left. Write a reason to log this session anyway.`
    }
    if (!formData.tutorId) return 'Please select a tutor.'
    if (!formData.startedAt) return 'Please enter a start time.'
    if (!formData.endedAt) return 'Please enter an end time.'

    const startDate = new Date(formData.startedAt)
    const endDate = new Date(formData.endedAt)
    if (isNaN(startDate.getTime())) return 'The start time is not a valid date.'
    if (isNaN(endDate.getTime())) return 'The end time is not a valid date.'
    if (endDate.getTime() <= startDate.getTime()) return 'The end time must be after the start time.'

    return null
  }

  const handleAddSession = async () => {
    // Validate before doing anything so the user gets clear feedback.
    const validationError = validateSessionForm()
    if (validationError) {
      setFormError(validationError)
      return
    }

    setFormError(null)
    setSubmitting(true)
    try {
      const minutes = calculateMinutes(formData.startedAt, formData.endedAt)

      const { data: insertedSession, error: sessionError } = await supabase
        .from('sessions')
        .insert({
          student_id: formData.studentId,
          tutor_id: formData.tutorId,
          started_at: formData.startedAt,
          ended_at: formData.endedAt,
          minutes: minutes,
          notes: formData.notes,
          approval_status: 'pending', // New sessions start as pending
          override_reason: hasAdminRights(profile?.role) ? overrideReason.trim() || null : null
        })
        .select()
        .single()

      if (sessionError) throw sessionError

      // Raise a persistent admin alert + activity entry for the new session.
      const studentName = getStudentName(formData.studentId)
      const tutorName = getTutorName(formData.tutorId)
      if (insertedSession?.id) {
        await createNotification({
          type: 'session_approval',
          title: `Session awaiting approval`,
          message: `${tutorName} logged a ${minutes}-min session for ${studentName}.`,
          relatedTable: 'sessions',
          relatedId: insertedSession.id,
          route: '/dashboard/sessions',
          severity: 'warning',
        })
        await logActivity({
          scope: 'sessions',
          action: 'session_logged',
          summary: `${tutorName} logged a ${minutes}-min session for ${studentName}`,
          entityTable: 'sessions',
          entityId: insertedSession.id,
          metadata: { minutes, studentId: formData.studentId, tutorId: formData.tutorId },
          actor,
        })
      }

      // NOTE: Minutes are NOT deducted here anymore
      // They will be deducted when an admin approves the session
      alert('Session logged successfully! Waiting for admin approval before deducting minutes.')

      setShowAddModal(false)
      fetchSessions()
      fetchBalances()
      setOverrideReason('')
      setFormData({
        studentId: '',
        tutorId: '',
        startedAt: '',
        endedAt: '',
        notes: ''
      })
      setIsUsingTemplate(false)
    } catch (error: any) {
      // Surface the failure to the user instead of swallowing it silently.
      console.error('Error adding session:', error)
      if (isBalanceGuardError(error)) fetchBalances()
      const message =
        error?.message ||
        error?.error_description ||
        'Something went wrong while logging the session. Please try again or contact admin.'
      setFormError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const getStudentName = (id: string) => students.find(s => s.id === id)?.name || 'Unknown'
  const getTutorName = (id: string) => tutors.find(t => t.id === id)?.name || 'Unknown'

  const enhancedSessions = sessions.map(session => ({
    ...session,
    studentName: getStudentName(session.studentId),
    tutorName: getTutorName(session.tutorId)
  }))

  const filteredSessions = enhancedSessions.filter(session =>
    session.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.tutorName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sessions</h1>
        <div className="flex flex-wrap gap-2">
          <ActivityClock scope="sessions" />
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FileDown className="h-5 w-5 mr-2" />
            Export CSV
          </button>
          <div className="relative">
            <button
              onClick={() => setShowColumnFilter(!showColumnFilter)}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Filter className="h-5 w-5 mr-2" />
              Columns
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {showColumnFilter && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-100 rounded-lg shadow-lg z-10 p-3">
                <div className="space-y-2">
                  {Object.entries(visibleColumns)
                    .filter(([key]) => key !== 'actions' || hasAdminRights(profile?.role))
                    .map(([key, value]) => (
                      <label key={key} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })}
                          className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                          {key}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => {
              console.log('Log Session clicked', { profile, tutors })
              setFormError(null)
              // Reset form but keep tutor ID if user is a tutor
              if (profile?.role === 'tutor' && profile?.email) {
                const currentTutor = tutors.find(t =>
                  t.email?.toLowerCase() === profile.email?.toLowerCase()
                )
                console.log('Finding tutor:', { email: profile.email, found: currentTutor })
                if (!currentTutor) {
                  alert(`No tutor account found for email: ${profile.email}. Please contact admin.`)
                  return
                }
                setFormData({
                  studentId: '',
                  tutorId: currentTutor.id,
                  startedAt: '',
                  endedAt: '',
                  notes: ''
                })
              } else {
                setFormData({
                  studentId: '',
                  tutorId: '',
                  startedAt: '',
                  endedAt: '',
                  notes: ''
                })
              }
              setShowAddModal(true)
            }}
            className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            Log Session
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search sessions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-100 text-gray-900 dark:text-white"
        />
      </div>

      {/* Sessions Table */}
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-surface-0">
            <tr>
              {visibleColumns.date && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
              )}
              {visibleColumns.timestamp && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Timestamp
                </th>
              )}
              {visibleColumns.student && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Student
                </th>
              )}
              {visibleColumns.tutor && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tutor
                </th>
              )}
              {visibleColumns.duration && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Duration
                </th>
              )}
              {visibleColumns.time && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Time
                </th>
              )}
              {visibleColumns.notes && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Notes
                </th>
              )}
              {visibleColumns.report && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Report
                </th>
              )}
              {visibleColumns.status && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
              )}
              {visibleColumns.actions && hasAdminRights(profile?.role) && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-surface-100 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredSessions.map((session) => (
              <tr key={session.id}>
                {visibleColumns.date && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      {format(new Date(session.startedAt), 'MMM d, yyyy')}
                    </div>
                  </td>
                )}
                {visibleColumns.timestamp && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      {session.createdAt
                        ? format(new Date(session.createdAt), 'MMM d, h:mm a')
                        : 'N/A'}
                    </div>
                  </td>
                )}
                {visibleColumns.student && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {session.studentId ? (
                      <Link
                        to={`/dashboard/students/${session.studentId}`}
                        className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                      >
                        {session.studentName}
                      </Link>
                    ) : (
                      <span className="text-gray-900 dark:text-white">{session.studentName}</span>
                    )}
                  </td>
                )}
                {visibleColumns.tutor && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {session.tutorName}
                  </td>
                )}
                {visibleColumns.duration && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400" />
                      {session.minutes} min
                    </div>
                  </td>
                )}
                {visibleColumns.time && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {format(new Date(session.startedAt), 'h:mm a')} - {format(new Date(session.endedAt), 'h:mm a')}
                  </td>
                )}
                {visibleColumns.notes && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {session.notes ? (
                      <button
                        onClick={() => setViewingNotesSession(session)}
                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-300 bg-brand-50 hover:bg-brand-100 dark:bg-brand-900/20 dark:hover:bg-brand-900/30 rounded-lg transition-colors"
                      >
                        <FileText className="h-4 w-4 mr-1.5" />
                        View Notes
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                )}
                {visibleColumns.report && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {session.reportUrl ? (
                      <a
                        href={session.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center text-brand-600 hover:text-brand-800 dark:text-brand-400 dark:hover:text-brand-600"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        {session.reportFilename || 'Report'}
                      </a>
                    ) : (
                      <label className="flex items-center cursor-pointer text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400">
                        <Upload className="h-4 w-4 mr-1" />
                        <span>Upload</span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) handleUploadReport(session.id, file)
                          }}
                        />
                      </label>
                    )}
                  </td>
                )}
                {visibleColumns.status && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {session.lifecycleStatus === 'cancelled' ? (
                      <span
                        title={session.cancelReason || 'Session cancelled'}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                      >
                        <Ban className="h-3 w-3 mr-1" />
                        Cancelled
                      </span>
                    ) : (
                      <>
                        {session.approvalStatus === 'pending' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Pending
                          </span>
                        )}
                        {session.approvalStatus === 'approved' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Approved
                          </span>
                        )}
                        {session.approvalStatus === 'rejected' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Rejected
                          </span>
                        )}
                        {session.overBalance && (
                          <span
                            title={session.overrideReason ? `Over balance: ${session.overrideReason}` : 'Logged with no minutes left'}
                            className="ml-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                          >
                            <ShieldAlert className="h-3 w-3 mr-1" />
                            Over balance
                          </span>
                        )}
                      </>
                    )}
                  </td>
                )}
                {visibleColumns.actions && hasAdminRights(profile?.role) && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      {session.lifecycleStatus !== 'cancelled' && (
                        <>
                          {session.approvalStatus === 'pending' && (
                            <>
                              <button
                                onClick={() => requestApprove(session)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectSession(session.id)}
                                className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              >
                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => { setCancelReason(''); setCancellingSession(session) }}
                            className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-surface-0 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            title="Cancel session"
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" />
                            Cancel
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeleteSession(session)}
                        className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-surface-0 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        title="Delete session"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredSessions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">No sessions found</p>
          </div>
        )}
      </div>

      {/* Add Session Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">Log New Session</h2>
            <div className="space-y-5">
              {/* Student Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Student <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.studentId}
                  onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select Student</option>
                  {students.map(student => {
                    const bal = balances.get(student.id)
                    const blocked = isLogBlocked(bal)
                    return (
                      <option key={student.id} value={student.id} disabled={blocked && !hasAdminRights(profile?.role)}>
                        {frozenStudentIds.has(student.id)
                          ? `🔒 ${student.name} (rate pending)`
                          : blocked
                            ? `⛔ ${student.name} (no minutes left)`
                            : bal
                              ? `${student.name} (${sessionsLabel(bal.availableMinutes, bal.sessionLengthMinutes)} left)`
                              : student.name}
                      </option>
                    )
                  })}
                </select>
                {formData.studentId && frozenStudentIds.has(formData.studentId) && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                    <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>Logging is paused for this student until an admin approves or rejects the pending rate-change request.</span>
                  </div>
                )}
                {formData.studentId && balances.get(formData.studentId) && (() => {
                  const bal = balances.get(formData.studentId)!
                  const blocked = isLogBlocked(bal)
                  return (
                    <>
                      <p className={`mt-2 text-xs ${blocked ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                        Balance {bal.balanceMinutes} min
                        {bal.pendingMinutes > 0 && ` · ${bal.pendingMinutes} min waiting for approval`}
                        {' · '}{sessionsLabel(bal.availableMinutes, bal.sessionLengthMinutes)} available
                      </p>
                      {blocked && !hasAdminRights(profile?.role) && (
                        <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
                          <Ban className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>This student has no minutes left, so you can't log a session. The office has to collect a top-up first.</span>
                        </div>
                      )}
                      {blocked && hasAdminRights(profile?.role) && (
                        <div className="mt-2 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                          <p className="flex items-start gap-2">
                            <ShieldAlert className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <span>This student has no minutes left. Tutors can't log for them; as an admin you can, with a reason.</span>
                          </p>
                          <textarea
                            rows={2}
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="Reason (required), e.g. Family paying by e-transfer on Friday"
                            className="w-full px-3 py-2 border border-amber-300 dark:border-amber-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>

              {/* Tutor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tutor <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.tutorId}
                  onChange={(e) => setFormData({ ...formData, tutorId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-800 dark:disabled:text-gray-400"
                  disabled={profile?.role === 'tutor'}
                  required
                >
                  <option value="">Select Tutor</option>
                  {tutors.map(tutor => (
                    <option key={tutor.id} value={tutor.id}>{tutor.name}</option>
                  ))}
                </select>
                {profile?.role === 'tutor' && (
                  <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                    ✓ Auto-selected based on your account
                  </p>
                )}
              </div>

              {/* Time Section with Clear Labels */}
              <div className="bg-gray-50 dark:bg-surface-0 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  Session Time
                </h3>

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startedAt}
                    onChange={(e) => setFormData({ ...formData, startedAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    When did the session begin?
                  </p>
                </div>

                {/* End Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.endedAt}
                    onChange={(e) => setFormData({ ...formData, endedAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    When did the session end?
                  </p>
                </div>

                {/* Duration Display (if both times are set) */}
                {formData.startedAt && formData.endedAt && (
                  <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-md p-3">
                    <p className="text-sm font-medium text-brand-900 dark:text-brand-100">
                      Session Duration: {
                        Math.round((new Date(formData.endedAt).getTime() - new Date(formData.startedAt).getTime()) / 60000)
                      } minutes
                    </p>
                  </div>
                )}
              </div>

              {/* Session Notes */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Session Report
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const template = generateSessionTemplate()
                      setFormData({ ...formData, notes: template })
                      setIsUsingTemplate(true)
                    }}
                    className="text-xs px-3 py-1 bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 rounded-md hover:bg-brand-200 dark:hover:bg-brand-900/50 transition-colors"
                  >
                    {isUsingTemplate ? 'Refresh Template' : 'Use Template'}
                  </button>
                </div>
                <textarea
                  placeholder="Click 'Use Template' to start with a structured report format, or enter your own notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white font-mono text-sm min-h-[240px] sm:min-h-[400px]"
                  rows={12}
                />
                <div className="mt-2 flex items-start space-x-2">
                  <svg className="w-4 h-4 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Use the template for comprehensive session documentation including preparation, feedback, and follow-up details.
                  </p>
                </div>
              </div>
            </div>
            {formError && (
              <div
                role="alert"
                className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
              >
                <XCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <span>{formError}</span>
              </div>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false)
                  setIsUsingTemplate(false)
                  setFormError(null)
                  setFormData({
                    studentId: '',
                    tutorId: '',
                    startedAt: '',
                    endedAt: '',
                    notes: ''
                  })
                }}
                disabled={submitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSession}
                disabled={submitting || (!hasAdminRights(profile?.role) && isLogBlocked(balances.get(formData.studentId)))}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {submitting && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {submitting ? 'Logging…' : 'Log Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {approvingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
                <ShieldAlert className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Approve with no minutes left?</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getStudentName(approvingSession.studentId)} · balance {balances.get(approvingSession.studentId)?.balanceMinutes ?? 0} min · this session {approvingSession.minutes} min
                </p>
              </div>
            </div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              value={approveReason}
              onChange={(e) => setApproveReason(e.target.value)}
              placeholder="e.g. Paid cash at the session, top-up being recorded"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => { setApprovingSession(null); setApproveReason('') }}
                disabled={approveSubmitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproveWithReason}
                disabled={approveSubmitting || !approveReason.trim()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {approveSubmitting ? 'Approving…' : 'Approve anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Notes Modal */}
      {viewingNotesSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Session Notes</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {viewingNotesSession.studentName} • {format(new Date(viewingNotesSession.startedAt), 'MMM d, yyyy')}
                </p>
              </div>
              <button
                onClick={() => setViewingNotesSession(null)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-surface-0 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {viewingNotesSession.notes}
              </pre>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => setViewingNotesSession(null)}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Session Modal */}
      {cancellingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-100 rounded-lg w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <Ban className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cancel session</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {cancellingSession.studentName} • {format(new Date(cancellingSession.startedAt), 'MMM d, yyyy')}
                </p>
              </div>
            </div>

            {cancellingSession.approvalStatus === 'approved' && cancellingSession.lifecycleStatus !== 'cancelled' && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                This session was approved — cancelling will restore {cancellingSession.minutes} minutes to {cancellingSession.studentName}.
              </div>
            )}

            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="e.g. Student no-show, rescheduled…"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white text-sm"
            />

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => { setCancellingSession(null); setCancelReason('') }}
                disabled={cancelSubmitting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Keep session
              </button>
              <button
                onClick={handleCancelSession}
                disabled={cancelSubmitting}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {cancelSubmitting && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {cancelSubmitting ? 'Cancelling…' : 'Cancel session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}